import React, { useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
} from "lexical";
import { $patchStyleText } from "@lexical/selection";
import { Button, Popover, Box, Tooltip } from "@mui/material";
import {
  FormatColorText,
  FormatColorFill,
  FormatPaint,
} from "@mui/icons-material";
import { TextFields } from "@mui/icons-material"; // Clean "T" icon without underline

export const TEXT_COLOR_COMMAND: LexicalCommand<string> =
  createCommand("TEXT_COLOR_COMMAND");
export const BACKGROUND_COLOR_COMMAND: LexicalCommand<string> = createCommand(
  "BACKGROUND_COLOR_COMMAND"
);

const COLORS = [
  "#000000",
  "#ffffff",
  "#ff0000",
  "#00ff00",
  "#0000ff",
  "#ffff00",
  "#ff00ff",
  "#00ffff",
  "#800000",
  "#008000",
  "#000080",
  "#800080",
  "#808000",
  "#008080",
  "#808080",
  "#c0c0c0",
  "#ff6b6b",
  "#4ecdc4",
  "#45b7d1",
  "#96ceb4",
  "#feca57",
  "#ff9ff3",
  "#54a0ff",
  "#5f27cd",
];

interface ColorPickerProps {
  onColorChange: (color: string) => void;
  buttonIcon: React.ReactNode;
  label: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  onColorChange,
  buttonIcon,
  label,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    onColorChange(color);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={label}>
        <Button
          onClick={handleClick}
          size="small"
          sx={{
            minWidth: "auto",
            padding: "4px 8px",
            color: "rgba(0, 0, 0, 0.54)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.03)",
            },
          }}
        >
          {buttonIcon}
        </Button>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)",
            gap: 1,
          }}
        >
          {COLORS.map((color) => (
            <Box
              key={color}
              onClick={() => handleColorSelect(color)}
              sx={{
                width: 24,
                height: 24,
                backgroundColor: color,
                border: "1px solid #ccc",
                cursor: "pointer",
                borderRadius: "4px",
                "&:hover": {
                  transform: "scale(1.1)",
                },
              }}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

export const TextColorPicker: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [currentColor, setCurrentColor] = useState<string>("#000000"); // default black

  const handleTextColorChange = (color: string) => {
    setCurrentColor(color);
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { color });
      }
    });
  };

  return (
    <ColorPicker
      onColorChange={handleTextColorChange}
      buttonIcon={
        <div
          style={{
            position: "relative",
            display: "inline-block",
            textAlign: "center",
            height: "26px",
            width: "18px",
            lineHeight: 1.25,
          }}
        >
          {/* <TextFields style={{ fontSize: 22 }} /> */}
          <span style={{ fontSize: 20, fontWeight: "bold" }}>A</span>
          <div
            style={{
              position: "absolute",
              bottom: "0.5px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "18px",
              height: "3px",
              backgroundColor: currentColor,
              borderRadius: "1px",
            }}
          />
        </div>
      }
      label="Text Color"
    />
  );
};

export const BackgroundColorPicker: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  const [bgCurrentColor, setBgCurrentColor] = useState<string>("#000000"); // default white

  const handleBackgroundColorChange = (color: string) => {
    setBgCurrentColor(color);
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyleText(selection, { "background-color": color });
      }
    });
  };

  return (
    <ColorPicker
      onColorChange={handleBackgroundColorChange}
      buttonIcon={
        <div
          style={{
            position: "relative",
            display: "inline-block",
            textAlign: "center",
            height: "26px",
            width: "18px",
            lineHeight: 1.25,
          }}
        >
          <span style={{ fontSize: 20, fontWeight: "bold" }}>A</span>
          <div
            style={{
              position: "absolute",
              bottom: "0.5px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 18,
              height: 3,
              backgroundColor: bgCurrentColor,
              borderRadius: 1,
            }}
          />
        </div>
      }
      label="Background Color"
    />
  );
};

export default function ColorPlugin(): null {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    const removeTextColorCommand = editor.registerCommand(
      TEXT_COLOR_COMMAND,
      (color: string) => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, { color });
          }
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeBackgroundColorCommand = editor.registerCommand(
      BACKGROUND_COLOR_COMMAND,
      (color: string) => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, { "background-color": color });
          }
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      removeTextColorCommand();
      removeBackgroundColorCommand();
    };
  }, [editor]);

  return null;
}
